import React, { useEffect, useState } from "react";
import { useQuery } from "../../hooks/queryParam";
import { API } from "../../api";
import { useForm } from "react-hook-form";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import {
  americanexp,
  card2,
  card3,
  checkoutLogo,
  circleBack,
  discover,
  logo,
  secure,
} from "../../assets";
import CheckoutForm from "../../components/stripe/CheckoutForm";
import StepSignUP from "../../components/general/Modal/StepSignUP";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { errorToast } from "../../hooks/useToast";

const Checkout = () => {
  const query = useQuery();
  const id = Number(query.get("id"));
  const price = query.get("price");
  const subscriptionType = query.get("type"); // Get subscription type from URL
  const navigate = useNavigate();

  const authorized = useSelector((state) => state.user.user.authorized);

  const [item, setItem] = useState(null);
  const { register, getValues } = useForm();
  const [promoError, setPromoError] = useState("");
  const [promoPrice, setPromoPrice] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [stripeElementsKey, setStripeElementsKey] = useState(0);
  const [isProcessingSubscription, setIsProcessingSubscription] = useState(false);

  // Auto-register subscription when user is authorized and has subscription ID
  const autoRegisterSubscription = async () => {
    if (authorized && id && !isProcessingSubscription) {
      setIsProcessingSubscription(true);
      try {
        // Use the subscription type from URL parameters instead of guessing
        let response;
        if (subscriptionType === "recall") {
          response = await API.registerSubscriptionRecall({ subscriptionRecallId: id });
        } else {
          response = await API.registerSubscription({ subscriptionId: id });
        }
        
        // Redirect to the payment URL returned by the API
        if (response?.data?.data) {
          window.location.assign(response.data.data);
        } else {
          // If no redirect URL, stay on checkout page
          setIsProcessingSubscription(false);
        }
      } catch (error) {
        console.error("Error auto-registering subscription:", error);
        errorToast(error, "Failed to process subscription");
        setIsProcessingSubscription(false);
      }
    }
  };

  const createNewPaymentIntent = async () => {
    let payload;
    if (subscriptionType === "recall") {
      payload = {
        subscriptionRecallId: Number(id),
        ...(getValues("promoCode") && { promoCode: getValues("promoCode") }),
      };
    } else {
      payload = {
        subscriptionId: Number(id),
        ...(getValues("promoCode") && { promoCode: getValues("promoCode") }),
      };
    }

    try {
      let key;
      if (subscriptionType === "recall") {
        key = await API.paymentIntendRecall(payload);
      } else {
        key = await API.paymentIntend(payload);
      }
      setClientSecret(key?.data?.data);
      setStripeElementsKey((prevKey) => prevKey + 1);
    } catch (error) {
      console.log("Error creating new PaymentIntent:", error);
    }
  };

  const handleApplyPromo = async () => {
    try {
      const promoCode = getValues("promoCode");
      if (!promoCode) {
        setPromoError("Please enter code");
        return;
      }

      let response;
      if (subscriptionType === "recall") {
        const apiMethod = authorized ? API.getTotalRecall : API.getTotalGeneralRecall;
        response = await apiMethod({
          subscriptionRecallId: id,
          promoCode: promoCode,
        });
      } else {
        const apiMethod = authorized ? API.getTotal : API.getTotalGeneral;
        response = await apiMethod({
          subscriptionId: id,
          promoCode: promoCode,
        });
      }

      setPromoPrice(response?.data?.data);
      setPromoError("");
      await createNewPaymentIntent();
    } catch (error) {
      console.log(error);
      setPromoPrice(null);
      setPromoError(error?.response?.data?.message);
    }
  };

  const getData = async () => {
    try {
      if (subscriptionType === "recall") {
        // Get recall subscription data
        const recallResponse = await API.getSingleSubscriptionRecall(id);
        if (recallResponse?.data?.data) {
          setItem({ ...recallResponse.data.data, type: "recall" });
          return;
        }
      } else {
        // Get regular subscription data
        const response = await API.getSingleSubscription(id);
        if (response?.data?.data) {
          setItem({ ...response.data.data, type: "regular" });
          return;
        }
      }
    } catch (error) {
      console.log("Error fetching subscription:", error);
    }
    
  };

  // Validate that the price from URL matches the actual subscription price
  const validatePrice = () => {
    if (price && item && item.price) {
      const urlPrice = parseFloat(price);
      const actualPrice = parseFloat(item.price);
      
      if (Math.abs(urlPrice - actualPrice) > 0.01) { // Allow for small floating point differences
        console.warn("Price mismatch detected:", { urlPrice, actualPrice });
        // You could show an error message or redirect here if needed
        // For now, we'll just log a warning
      }
    }
  };

  const getStripeKeys = async () => {
    try {
      let response;
      if (subscriptionType === "recall") {
        response = await API.getPublishKeyRecall();
      } else {
        response = await API.getPublishKey();
      }
      setStripePromise(loadStripe(response?.data?.publishableKey));
      if (authorized) {
        await createNewPaymentIntent();
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    // Validate that subscription type is present
    if (!subscriptionType) {
      console.error("Missing subscription type in URL parameters");
      navigate("/pricing");
      return;
    }
    
    getData();
    if (authorized) {
      getStripeKeys();
      autoRegisterSubscription();
    }
  }, [authorized, id, subscriptionType]);

  // Validate price after item data is loaded
  useEffect(() => {
    if (item && price) {
      validatePrice();
    }
  }, [item, price]);

  if (!item) return <div> </div>;

  // Show loading state while processing subscription
  if (isProcessingSubscription) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-themeButton-0 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Processing your subscription...</p>
        </div>
      </div>
    );
  }

  return (
    <div className=" ">
      <div className="flex justify-between items-center py-3 border-b ">
        <Link to={"/subscriptions"} className="hidden sm:block">
          <img
            src={circleBack}
            alt="back"
            width={200}
            height={100}
            className="max-w-[70px] pl-6 w-full mx-auto  "
          />
        </Link>

        <Link to={"/"} className="max-w-[220px] w-full mx-auto ">
          <img
            src={logo}
            alt="logo"
            width={200}
            height={100}
            className="max-w-[220px] w-full mx-auto "
          />
        </Link>
      </div>

      <div className="max-w-[750px] mx-auto px-3 py-2  ">
        <div className="flex   flex-col gap-[2px]">
          <h2 className="text-2xl font-bold   text-gray-800  md:mt-3 noUppercase">
            Payment Summary
          </h2>
          <p>Review the information below regarding this transaction.</p>
       
        </div>

        <div
          className={`flex flex-col ${
            authorized ? "" : "flex-row-reverse"
          } gap-2`}
        >
          <div className="flex flex-col gap-4 mt-4 border-2   w-full">
            <table className="w-full   border-collapse">
              <thead className="bg-[#F5F5F5]">
                <tr className="border-b">
                  <th className="text-center py-[5px] font-normal text-lg border-r border-gray-300">
                    Sr#
                  </th>
                  <th className="text-center py-[5px] font-normal text-lg border-r border-gray-300">
                    Description
                  </th>
                  <th className="text-center py-[5px] font-normal text-lg border-r border-gray-300">
                    Cost
                  </th>
                  <th className="text-center py-[5px] font-normal text-lg border-r border-gray-300">
                    Quantity
                  </th>
                  <th className="text-center py-[5px] font-normal text-lg  ">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2 text-center border-r border-gray-300">
                    1
                  </td>
                  <td className="py-2 text-center border-r border-gray-300">
                    {item.name} Package
                  </td>
                  <td className="text-center py-2 border-r border-gray-300">
                    ${item.price.toFixed(2)}
                  </td>
                  <td className="text-center py-2 border-r border-gray-300">
                    1
                  </td>
                  <td className="text-center py-2  ">
                    ${item.price.toFixed(2)}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="border-b">
                  <td colSpan="4" className="text-right pr-6 py-2">
                    Subtotal
                  </td>
                  <td className="text-center py-2">${item.price.toFixed(2)}</td>
                </tr>
                {/* <tr className="border-b">
                  <td colSpan="4" className="text-right pr-6 py-2">
                    Discount
                  </td>
                  <td className="text-center py-2">
                    {" "}
                    $
                    {promoPrice
                      ? parseFloat(item.price - promoPrice).toFixed(2)
                      : "0"}
                  </td>
                </tr> */}
                <tr>
                  <td colSpan="4" className="text-right pr-6 py-2 font-bold">
                    Grand Total
                  </td>

                  <td className="text-center py-2 font-bold">
                    {promoPrice ? promoPrice.toFixed(2) : item.price.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* <>
            <div className="w-full relative">
              <input
                type="text"
                placeholder="Add promo code (Optional)"
                className={`w-full placeholder:text-[14px] px-4 py-4 border border-gray-300 ${
                  promoError ? "border-red-400" : ""
                } 
              ${promoPrice ? "border-green-600" : ""}
              rounded-md focus:outline-none`}
                {...register("promoCode")}
              />
              <button
                onClick={handleApplyPromo}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-themeButton-0 text-white hover:bg-themeButtonHover-0 text-sm px-4 py-3 rounded-md font-semibold transition duration-300 ease-in-out"
                type="button"
              >
                Apply Promo
              </button>
            </div>
            {promoError && (
              <p className="text-sm text-red-500 pl-1">{promoError}</p>
            )}
            {promoPrice && (
              <p className="text-sm text-green-600">
                Promo Code has been applied
              </p>
            )}
          </> */}

          <div className="flex flex-col gap-2 ">
            {/* <h2 className="text-2xl font-bold   text-gray-800 mt-4 noUppercase">
              Billing Information
            </h2>
            <p className="capitalize">enter your payment details</p> */}

            <div className="flex justify-between mt-2 flex-wrap md:flex-nowrap items-center ">
              <h2 className="text-2xl font-bold    text-gray-800   noUppercase">
                Cards Accepted
              </h2>
              <div className=" flex  flex-nowrap  gap-1  ">
                <img
                  src={card2}
                  alt="card1"
                  width={100}
                  height={150}
                  className="max-w-[80px] w-full object-center shadow-lg"
                />
                <img
                  src={card3}
                  alt="card1"
                  width={100}
                  height={150}
                  className="max-w-[80px] w-full object-center shadow-lg"
                />
                <img
                  src={americanexp}
                  alt="card1"
                  width={100}
                  height={150}
                  className="max-w-[80px] w-full object-center shadow-lg"
                />
                <img
                  src={discover}
                  alt="card1"
                  width={100}
                  height={150}
                  className="max-w-[80px] w-full object-center shadow-lg"
                />
              </div>
            </div>
          </div>

          <div className="w-full">
            <div className="flex items-center gap-2 mt-3 justify-start">
              <h2 className="text-2xl font-bold   text-gray-800  noUppercase">
                Credit/Debit Card
              </h2>
              <div className="flex justify-start gap-1 items-center ">
                <div>
                  <img src={secure} alt="secure" width={18} height={18} />
                </div>
                <p>Secure Checkout</p>
              </div>
            </div>

            {authorized ? (
              <div className="mt-5">
                {clientSecret && stripePromise && (
                  <Elements
                    key={stripeElementsKey}
                    stripe={stripePromise}
                    options={{ clientSecret }}
                  >
                    <CheckoutForm 
                      clientSecret={clientSecret} 
                      totalPrice={promoPrice ? promoPrice.toFixed(2) : item.price.toFixed(2)}
                    />
                  </Elements>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
